import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Search, ShieldAlert, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Column, DataTable } from "@/components/ui/data-table";
import { ApiError } from "@/services/BaseService";
import { RegionResponseDto } from "@/types/admin-region";
import { useAdminRegions, useBootstrapRegions } from "@/hooks/useAdminRegions";
import { cn } from "@/lib/utils";
import { BootstrapConfirmDialog } from "./components/BootstrapConfirmDialog";
import { BootstrapResultDialog } from "./components/BootstrapResultDialog";
import { RegionDetailDialog } from "./components/RegionDetailDialog";
import { usePlatformPermissions } from "@/hooks/usePlatformPermissions";

type StatusFilter = "all" | "active" | "inactive";

const STATUS_FILTERS: StatusFilter[] = ["all", "active", "inactive"];

export const RegionSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { canAdminister } = usePlatformPermissions();
  const canWrite = canAdminister("settings");
  const { data: regions, isLoading, isError, refetch } = useAdminRegions();
  const bootstrapMutation = useBootstrapRegions();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bootstrapResult, setBootstrapResult] = useState<Awaited<
    ReturnType<typeof bootstrapMutation.mutateAsync>
  > | null>(null);
  const [activeRegion, setActiveRegion] = useState<RegionResponseDto | null>(
    null,
  );

  const filteredRegions = useMemo(() => {
    if (!regions) return [];
    const term = search.trim().toLowerCase();
    return regions.filter((region) => {
      if (statusFilter === "active" && !region.active) return false;
      if (statusFilter === "inactive" && region.active) return false;
      if (!term) return true;
      return (
        region.code.toLowerCase().includes(term) ||
        region.name.toLowerCase().includes(term)
      );
    });
  }, [regions, statusFilter, search]);

  const isFiltering = search.trim().length > 0 || statusFilter !== "all";

  const handleConfirmBootstrap = async () => {
    try {
      const result = await bootstrapMutation.mutateAsync();
      setConfirmOpen(false);
      setBootstrapResult(result);
    } catch (error) {
      const message =
        error instanceof ApiError || error instanceof Error
          ? error.message
          : t("regions.bootstrap.failure");
      toast.error(message);
      setConfirmOpen(false);
    }
  };

  const renderEndpoint = (region: RegionResponseDto): React.ReactNode => {
    const { endpoint, port, useSSL } = region.storage;
    if (!endpoint) return "—";
    const scheme = useSSL ? "https://" : "";
    const portPart = port ? `:${port}` : "";
    return (
      <span className="font-mono text-xs text-muted-foreground">
        {`${scheme}${endpoint}${portPart}`}
      </span>
    );
  };

  const columns: Column<RegionResponseDto>[] = [
    {
      key: "code",
      header: t("regions.table.code"),
      render: (_, row) => <span className="font-mono text-xs">{row.code}</span>,
    },
    {
      key: "name",
      header: t("regions.table.name"),
    },
    {
      key: "active",
      header: t("regions.table.status"),
      render: (_, row) => (
        <Badge variant={row.active ? "new_default" : "new_secondary"}>
          {row.active
            ? t("regions.status.active")
            : t("regions.status.inactive")}
        </Badge>
      ),
    },
    {
      key: "storage.type",
      header: t("regions.table.storageType"),
      render: (_, row) => (
        <Badge variant="new_outline">
          {t(`regions.storageType.${row.storage.type}`)}
        </Badge>
      ),
    },
    {
      key: "endpoint",
      header: t("regions.table.endpoint"),
      render: (_, row) => renderEndpoint(row),
    },
    {
      key: "storage.bucket",
      header: t("regions.table.bucket"),
      render: (_, row) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.storage.bucket}
        </span>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="p-12 text-sm text-muted-foreground">
        {t("regions.page.loading")}
      </div>
    );
  }

  if (isError || !regions) {
    return (
      <div className="flex flex-col gap-3 p-12 max-w-md">
        <div className="flex items-center gap-2 text-destructive">
          <ShieldAlert className="size-5" />
          <span className="text-sm font-medium">
            {t("regions.page.loadError")}
          </span>
        </div>
        <Button
          type="button"
          variant="new_outline"
          size="sm"
          onClick={() => refetch()}
          className="w-fit"
        >
          {t("regions.page.retry")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-10 p-12 max-w-6xl">
      <header className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex flex-col gap-2 max-w-3xl">
            <h1 className="text-2xl font-semibold text-foreground">
              {t("regions.page.title")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t("regions.page.description")}
            </p>
          </div>
          <Button
            type="button"
            variant="new_default"
            size="default"
            onClick={() => setConfirmOpen(true)}
            disabled={!canWrite || bootstrapMutation.isPending}
          >
            <Sparkles className="size-4" />
            {t("regions.bootstrap.button")}
          </Button>
        </div>
      </header>

      <section className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[260px] max-w-md">
            <Input
              type="search"
              icon={<Search className="size-4 text-muted-foreground" />}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("regions.filters.search")}
            />
          </div>
          <div className="flex items-center gap-2" role="radiogroup">
            {STATUS_FILTERS.map((value) => (
              <Button
                key={value}
                type="button"
                role="radio"
                aria-checked={statusFilter === value}
                variant={statusFilter === value ? "new_secondary" : "new_ghost"}
                size="sm"
                onClick={() => setStatusFilter(value)}
                className={cn(
                  "min-w-[80px]",
                  statusFilter === value && "font-semibold",
                )}
              >
                {t(`regions.filters.${value}`)}
              </Button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto rounded-md border border-base-border">
          <DataTable
            data={filteredRegions}
            columns={columns}
            getRowKey={(row) => row.code}
            withPagination={false}
            withSorting={false}
            emptyMessage={
              isFiltering
                ? t("regions.table.emptyFiltered")
                : t("regions.table.empty")
            }
            onRowClick={(row) => setActiveRegion(row)}
          />
        </div>
      </section>

      <BootstrapConfirmDialog
        open={confirmOpen}
        isPending={bootstrapMutation.isPending}
        onConfirm={handleConfirmBootstrap}
        onCancel={() => setConfirmOpen(false)}
      />

      <BootstrapResultDialog
        result={bootstrapResult}
        onClose={() => setBootstrapResult(null)}
      />

      <RegionDetailDialog
        region={activeRegion}
        onClose={() => setActiveRegion(null)}
      />
    </div>
  );
};
